/**
 * Travel policy evaluation (ADR 0030, plan §5.7, D7). ONE pure function, run live in the trip form
 * and again on the server; the server's result is snapshotted on the trip (`policyResult`) and its
 * violation codes feed the approval's workflow context. Pure and RN-safe; money is decimal.js on
 * decimal strings (CLAUDE.md invariant 2).
 *
 * The caller resolves everything that needs I/O and passes it in: the policy version in force on
 * the trip's START date (D12, `policyInForce`), the traveller's grade rank on that date, "today" in
 * the traveller's location timezone (EC-50), and the leave / trip / on-duty rows the trip may
 * collide with. Nothing here reads a clock.
 *
 * Severities (D24): a `soft` violation needs a reason from the traveller (`requiresJustification`);
 * a `hard` one blocks submission unless a `travel:approve` holder overrides it later, with a reason.
 *
 * Grade bands: an entitlement row covers `gradeRankMin <= rank <= gradeRankMax`, a null bound being
 * open. Rank is higher for more senior grades, as `GradeCatalogue.rank` is. A traveller with no
 * grade on the start date gets the LOWEST band (the least generous rows) and `gradeMissing` is set
 * so HR sees a data warning on the trip (EC-49).
 */

import { Decimal } from 'decimal.js';
import type {
  TravelBookingMode,
  TravelCityTier,
  TravelClass,
  TravelEntitlementMode,
  TravelLegMode,
  TravelPolicyViolationSeverity,
} from '../enums.js';
import { TRAVEL_CITY_TIERS } from '../enums.js';
import { compareDateKeys, diffDays, isWithin } from './dates.js';
import {
  computePerDiem,
  type PerDiemMealsProvided,
  type PerDiemRates,
  type PerDiemResult,
  type PerDiemRule,
} from './per-diem.js';
import { findTripOverlaps, normalizeCityKey, type TripCommitments, type TripOverlapKind } from './overlap.js';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/** One `TravelEntitlement` row, amounts as decimal strings. */
export interface TravelEntitlementRow {
  mode: TravelEntitlementMode;
  gradeRankMin: number | null;
  gradeRankMax: number | null;
  /** Empty means the mode has no class restriction. */
  allowedClasses: readonly TravelClass[];
  /** Null: the row applies to every city tier (a tier-specific row for the same tier wins). */
  cityTier: TravelCityTier | null;
  /** Hotel: nightly cap. Per diem / lodging in lieu: the daily amount. Null: no cap. */
  capAmount: string | null;
  selfBookingAllowed: boolean;
  /** Classes nobody in this band may book without a `travel:approve` override. */
  hardBlockedClasses: readonly TravelClass[];
}

/** The policy version in force on the trip's start date, flattened for evaluation. */
export interface TravelPolicySnapshot {
  currency: string;
  /** Minimum whole days between today and departure; 0 for none. */
  leadTimeDays: number;
  /** How long after a trip a post-facto request is still accepted (D14). */
  postFactoMaxDays: number;
  /** Percent (0–100) of the trip estimate an advance may be. Null: no percentage cap. */
  maxAdvancePct: string | null;
  maxAdvanceAmount: string | null;
  entitlements: readonly TravelEntitlementRow[];
  /** Null when the policy has no per-diem rule: no per diem is estimated. */
  perDiemRule: PerDiemRule | null;
  /** City tiers of the trip's country, keyed by `normalizeCityKey`. A city not listed is `tier_3` (D26). */
  cityTiers: Readonly<Record<string, TravelCityTier>>;
}

export interface PolicyTripLeg {
  mode: TravelLegMode;
  travelClass?: TravelClass | null;
  /** Travel day, local. */
  date: string;
  fromCity?: string | null;
  toCity?: string | null;
}

export interface PolicyTripStay {
  city: string;
  /** First night. */
  checkIn: string;
  /** Morning of leaving. */
  checkOut: string;
  /** Estimated rate per night, decimal string. */
  nightlyRate?: string | null;
  /** Overrides the city lookup (a tier the traveller's stay already carries). */
  cityTier?: TravelCityTier | null;
  lodgingInLieu?: boolean;
}

export interface PolicyTrip {
  startDate: string;
  endDate: string;
  departureTime?: string | null;
  returnTime?: string | null;
  isPostFacto: boolean;
  bookingMode: TravelBookingMode;
  /** The grade rank on the start date; null when the traveller has none (EC-49). */
  gradeRank: number | null;
  legs: readonly PolicyTripLeg[];
  stays: readonly PolicyTripStay[];
  personalDays: readonly string[];
  mealsProvided?: readonly PerDiemMealsProvided[];
  /** The advance asked for with the trip, decimal string. */
  advanceAmount?: string | null;
  /** The trip's total estimate, which an advance percentage is measured against. */
  estimatedAmount?: string | null;
  /** Leave, other trips and on-duty rows that may overlap; the trip's own rows left out. */
  commitments?: TripCommitments;
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

/** Stable codes: they are stored in `policyResult` and are workflow-condition values. */
export const TRAVEL_POLICY_VIOLATION_CODES = [
  'end_before_start',
  'leg_outside_trip',
  'stay_outside_trip',
  'start_in_past',
  'post_facto_not_started',
  'post_facto_too_old',
  'lead_time_short',
  'mode_not_entitled',
  'class_not_allowed',
  'class_blocked',
  'hotel_over_cap',
  'self_booking_not_allowed',
  'advance_over_amount',
  'advance_over_pct',
  'personal_day_outside_trip',
] as const;
export type TravelPolicyViolationCode = (typeof TRAVEL_POLICY_VIOLATION_CODES)[number];

export interface TravelPolicyViolation {
  code: TravelPolicyViolationCode;
  severity: TravelPolicyViolationSeverity;
  /** Where in the trip it applies: `startDate`, `legs[1].travelClass`, `stays[0].nightlyRate`… */
  field: string;
  /** The rule's limit and what the trip has, as strings (money as decimal strings). */
  limit?: string;
  actual?: string;
  message: string;
}

export interface TravelPolicyWarning {
  code: 'overlap_leave' | 'overlap_trip' | 'overlap_on_duty';
  /** The row the trip overlaps. */
  refId: string;
  dates: string[];
  message: string;
}

export interface TravelStayLimit {
  index: number;
  cityTier: TravelCityTier;
  /** The nightly cap that applies, decimal string, or null for none. Snapshotted on the stay. */
  nightlyCap: string | null;
}

export interface TravelEntitlementSummary {
  /** The rank the rows were matched on; null when the lowest band is open-ended below. */
  gradeRankUsed: number | null;
  /** The rows that apply to this traveller. */
  rows: TravelEntitlementRow[];
  /** True when any applicable row allows the traveller to book for themself (D1). */
  selfBookingAllowed: boolean;
}

export interface TravelPolicyEvaluation {
  entitlements: TravelEntitlementSummary;
  violations: TravelPolicyViolation[];
  warnings: TravelPolicyWarning[];
  /** A soft violation or a post-facto trip needs the traveller's reason. */
  requiresJustification: boolean;
  /** Null when the policy pays no per diem. */
  estimatedPerDiem: PerDiemResult | null;
  stayLimits: TravelStayLimit[];
  /** EC-49: no grade on the start date, the lowest band was used. */
  gradeMissing: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** A city's tier; an unlisted city, or a blank one, is `tier_3` (D26). */
export function resolveCityTier(
  city: string,
  cityTiers: Readonly<Record<string, TravelCityTier>>,
): TravelCityTier {
  return cityTiers[normalizeCityKey(city)] ?? 'tier_3';
}

interface Effective {
  validFrom: string;
  validTo: string | null;
  legalEntityId: string | null;
}

/**
 * The policy row in force on `asOf` (the trip's start date, D12): the one for the traveller's legal
 * entity if there is one, else the organisation-wide one, and of those the latest `validFrom`.
 * `validTo` is inclusive. Null when nothing is in force.
 */
export function policyInForce<T extends Effective>(
  policies: readonly T[],
  asOf: string,
  legalEntityId: string | null,
): T | null {
  const live = policies.filter(
    (p) => p.validFrom <= asOf && (p.validTo === null || asOf <= p.validTo),
  );
  const pick = (rows: T[]): T | null =>
    rows.reduce<T | null>((best, p) => (best === null || p.validFrom > best.validFrom ? p : best), null);
  const own = legalEntityId === null ? [] : live.filter((p) => p.legalEntityId === legalEntityId);
  return pick(own) ?? pick(live.filter((p) => p.legalEntityId === null));
}

function covers(row: TravelEntitlementRow, rank: number): boolean {
  return (
    (row.gradeRankMin === null || rank >= row.gradeRankMin) &&
    (row.gradeRankMax === null || rank <= row.gradeRankMax)
  );
}

/** The rank of the lowest band: the smallest lower bound, `-Infinity` when a band is open below. */
function lowestBandRank(rows: readonly TravelEntitlementRow[]): number {
  if (rows.length === 0) return 0;
  return Math.min(...rows.map((r) => r.gradeRankMin ?? Number.NEGATIVE_INFINITY));
}

/** The row for a tier: an exact-tier row wins over an all-tiers row. */
function rowForTier(
  rows: readonly TravelEntitlementRow[],
  tier: TravelCityTier,
): TravelEntitlementRow | null {
  return rows.find((r) => r.cityTier === tier) ?? rows.find((r) => r.cityTier === null) ?? null;
}

function ratesFor(rows: readonly TravelEntitlementRow[]): PerDiemRates {
  const rates: Partial<Record<TravelCityTier, string | null>> = {};
  for (const tier of TRAVEL_CITY_TIERS) rates[tier] = rowForTier(rows, tier)?.capAmount ?? null;
  return rates;
}

/**
 * The entitlement rows that cover a grade rank, exactly as `evaluateTravelPolicy` picks them: a
 * traveller with no grade gets the lowest band (EC-49). Exported so the allowance drafts after a
 * trip (`travel/money.ts`) price with the same rows the estimate used.
 */
export function entitlementRowsForGrade(
  entitlements: readonly TravelEntitlementRow[],
  gradeRank: number | null,
): { rows: TravelEntitlementRow[]; gradeRankUsed: number | null; gradeMissing: boolean } {
  const rank = gradeRank ?? lowestBandRank(entitlements);
  return {
    rows: entitlements.filter((r) => covers(r, rank)),
    gradeRankUsed: Number.isFinite(rank) ? rank : null,
    gradeMissing: gradeRank === null,
  };
}

/**
 * One mode's amount per city tier (an exact-tier row wins over an all-tiers row): the per-diem
 * and lodging-in-lieu daily amounts, or the mileage rate per km. A tier with no row is `null`.
 */
export function entitlementRatesFor(
  rows: readonly TravelEntitlementRow[],
  mode: TravelEntitlementMode,
): PerDiemRates {
  return ratesFor(rows.filter((r) => r.mode === mode));
}

const OVERLAP_CODE: Record<TripOverlapKind, TravelPolicyWarning['code']> = {
  leave: 'overlap_leave',
  trip: 'overlap_trip',
  on_duty: 'overlap_on_duty',
};

const OVERLAP_LABEL: Record<TripOverlapKind, string> = {
  leave: 'leave',
  trip: 'another trip',
  on_duty: 'an on-duty request',
};

const money = (value: Decimal): string => value.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

/**
 * @param trip the trip as drafted
 * @param policy the policy version in force on the trip's start date
 * @param todayKey "today" as `YYYY-MM-DD` in the traveller's location timezone
 */
export function evaluateTravelPolicy(
  trip: PolicyTrip,
  policy: TravelPolicySnapshot,
  todayKey: string,
): TravelPolicyEvaluation {
  const violations: TravelPolicyViolation[] = [];
  const add = (v: TravelPolicyViolation): void => {
    violations.push(v);
  };

  // --- Grade band (EC-49) ---
  const gradeMissing = trip.gradeRank === null;
  const rank = trip.gradeRank ?? lowestBandRank(policy.entitlements);
  const rows = policy.entitlements.filter((r) => covers(r, rank));
  const rowsFor = (mode: TravelEntitlementMode): TravelEntitlementRow[] =>
    rows.filter((r) => r.mode === mode);
  const selfBookingAllowed = rows.some((r) => r.selfBookingAllowed);

  // --- Dates ---
  const datesOk = trip.startDate <= trip.endDate;
  if (!datesOk) {
    add({
      code: 'end_before_start',
      severity: 'hard',
      field: 'endDate',
      limit: trip.startDate,
      actual: trip.endDate,
      message: 'The trip cannot end before it starts.',
    });
  }

  trip.legs.forEach((leg, i) => {
    if (datesOk && !isWithin(leg.date, trip.startDate, trip.endDate)) {
      add({
        code: 'leg_outside_trip',
        severity: 'hard',
        field: `legs[${i}].date`,
        limit: `${trip.startDate}..${trip.endDate}`,
        actual: leg.date,
        message: 'A journey leg falls outside the trip dates.',
      });
    }
  });
  trip.stays.forEach((stay, i) => {
    if (datesOk && (stay.checkIn < trip.startDate || stay.checkOut > trip.endDate)) {
      add({
        code: 'stay_outside_trip',
        severity: 'hard',
        field: `stays[${i}].checkIn`,
        limit: `${trip.startDate}..${trip.endDate}`,
        actual: `${stay.checkIn}..${stay.checkOut}`,
        message: 'A hotel stay falls outside the trip dates.',
      });
    }
  });
  const personal = [...trip.personalDays].sort(compareDateKeys);
  personal.forEach((day, i) => {
    if (datesOk && !isWithin(day, trip.startDate, trip.endDate)) {
      add({
        code: 'personal_day_outside_trip',
        severity: 'hard',
        field: `personalDays[${i}]`,
        limit: `${trip.startDate}..${trip.endDate}`,
        actual: day,
        message: 'A personal day must fall within the trip dates.',
      });
    }
  });

  // --- When it is asked (D14): before the trip, or after it within the window ---
  const startedBeforeToday = trip.startDate < todayKey;
  if (!trip.isPostFacto && startedBeforeToday) {
    add({
      code: 'start_in_past',
      severity: 'hard',
      field: 'startDate',
      limit: todayKey,
      actual: trip.startDate,
      message: 'The trip has already started. File it as a post-facto request with a reason.',
    });
  }
  if (trip.isPostFacto && !startedBeforeToday) {
    // Truthful both ways: the flag skips the desk and the lead time, so it may only be set for a
    // trip that has actually begun.
    add({
      code: 'post_facto_not_started',
      severity: 'hard',
      field: 'isPostFacto',
      limit: todayKey,
      actual: trip.startDate,
      message: 'This trip has not started yet, so it cannot be filed as a post-facto request.',
    });
  }
  if (trip.isPostFacto && startedBeforeToday) {
    // Days since the trip finished; a trip still under way is 0 days late.
    const late = diffDays(trip.endDate < todayKey ? trip.endDate : todayKey, todayKey);
    if (late > policy.postFactoMaxDays) {
      add({
        code: 'post_facto_too_old',
        severity: 'hard',
        field: 'endDate',
        limit: String(policy.postFactoMaxDays),
        actual: String(late),
        message: `A post-facto request must be filed within ${policy.postFactoMaxDays} days of the trip.`,
      });
    }
  }
  if (!trip.isPostFacto && !startedBeforeToday && policy.leadTimeDays > 0) {
    const lead = diffDays(todayKey, trip.startDate);
    if (lead < policy.leadTimeDays) {
      add({
        code: 'lead_time_short',
        severity: 'soft',
        field: 'startDate',
        limit: String(policy.leadTimeDays),
        actual: String(lead),
        message: `Trips should be requested at least ${policy.leadTimeDays} days ahead.`,
      });
    }
  }

  // --- Legs: mode and class ---
  const unentitled = new Set<TravelEntitlementMode>();
  trip.legs.forEach((leg, i) => {
    const modeRows = rowsFor(leg.mode);
    if (modeRows.length === 0) {
      if (!unentitled.has(leg.mode)) {
        unentitled.add(leg.mode);
        add({
          code: 'mode_not_entitled',
          severity: 'soft',
          field: `legs[${i}].mode`,
          actual: leg.mode,
          message: `Your grade has no ${leg.mode} entitlement.`,
        });
      }
      return;
    }
    const cls = leg.travelClass;
    if (!cls) return;
    if (modeRows.some((r) => r.hardBlockedClasses.includes(cls))) {
      add({
        code: 'class_blocked',
        severity: 'hard',
        field: `legs[${i}].travelClass`,
        actual: cls,
        message: 'This class of travel is not permitted for your grade.',
      });
      return;
    }
    const restricted = modeRows.filter((r) => r.allowedClasses.length > 0);
    if (restricted.length > 0 && !restricted.some((r) => r.allowedClasses.includes(cls))) {
      const allowed = [...new Set(restricted.flatMap((r) => r.allowedClasses))];
      add({
        code: 'class_not_allowed',
        severity: 'soft',
        field: `legs[${i}].travelClass`,
        limit: allowed.join(','),
        actual: cls,
        message: 'This class of travel is above your entitlement.',
      });
    }
  });

  // --- Stays: hotel cap by city tier ---
  const hotelRows = rowsFor('hotel');
  const stayLimits: TravelStayLimit[] = [];
  trip.stays.forEach((stay, i) => {
    const tier = stay.cityTier ?? resolveCityTier(stay.city, policy.cityTiers);
    const row = rowForTier(hotelRows, tier);
    const cap = row?.capAmount ?? null;
    stayLimits.push({ index: i, cityTier: tier, nightlyCap: cap === null ? null : money(new Decimal(cap)) });
    if (stay.lodgingInLieu) return;
    if (hotelRows.length === 0) {
      if (!unentitled.has('hotel')) {
        unentitled.add('hotel');
        add({
          code: 'mode_not_entitled',
          severity: 'soft',
          field: `stays[${i}]`,
          actual: 'hotel',
          message: 'Your grade has no hotel entitlement.',
        });
      }
      return;
    }
    if (cap !== null && stay.nightlyRate != null && new Decimal(stay.nightlyRate).gt(cap)) {
      add({
        code: 'hotel_over_cap',
        severity: 'soft',
        field: `stays[${i}].nightlyRate`,
        limit: money(new Decimal(cap)),
        actual: money(new Decimal(stay.nightlyRate)),
        message: `The nightly rate is above the ${policy.currency} ${money(new Decimal(cap))} cap for a ${tier.replace('_', ' ')} city.`,
      });
    }
  });

  // --- Who books (D1) ---
  if (trip.bookingMode === 'self' && !selfBookingAllowed) {
    add({
      code: 'self_booking_not_allowed',
      severity: 'hard',
      field: 'bookingMode',
      limit: 'desk',
      actual: 'self',
      message: 'Your grade cannot book its own travel; the travel desk books it.',
    });
  }

  // --- Advance ---
  if (trip.advanceAmount != null && new Decimal(trip.advanceAmount).gt(0)) {
    const advance = new Decimal(trip.advanceAmount);
    if (policy.maxAdvanceAmount !== null && advance.gt(policy.maxAdvanceAmount)) {
      add({
        code: 'advance_over_amount',
        severity: 'soft',
        field: 'advanceAmount',
        limit: money(new Decimal(policy.maxAdvanceAmount)),
        actual: money(advance),
        message: 'The advance is above the policy maximum.',
      });
    }
    if (policy.maxAdvancePct !== null) {
      const allowed = new Decimal(trip.estimatedAmount ?? 0).mul(policy.maxAdvancePct).div(100);
      if (advance.gt(allowed)) {
        add({
          code: 'advance_over_pct',
          severity: 'soft',
          field: 'advanceAmount',
          limit: money(allowed),
          actual: money(advance),
          message: `The advance is above ${new Decimal(policy.maxAdvancePct).toFixed()}% of the trip estimate.`,
        });
      }
    }
  }

  // --- Overlaps: warnings only (EC-2, EC-3). Personal days are meant to be covered by leave. ---
  const warnings: TravelPolicyWarning[] = [];
  if (datesOk && trip.commitments) {
    const personalSet = new Set(personal);
    for (const overlap of findTripOverlaps(trip, trip.commitments)) {
      const dates = overlap.dates.filter((d) => !personalSet.has(d));
      if (dates.length === 0) continue;
      warnings.push({
        code: OVERLAP_CODE[overlap.kind],
        refId: overlap.id,
        dates,
        message: `The trip overlaps ${OVERLAP_LABEL[overlap.kind]} on ${dates.join(', ')}.`,
      });
    }
  }

  // --- Per diem estimate ---
  let estimatedPerDiem: PerDiemResult | null = null;
  const perDiemRows = rowsFor('per_diem');
  if (datesOk && policy.perDiemRule !== null && perDiemRows.length > 0) {
    const destination = trip.legs.find((l) => l.toCity)?.toCity;
    estimatedPerDiem = computePerDiem({
      startDate: trip.startDate,
      endDate: trip.endDate,
      departureTime: trip.departureTime,
      returnTime: trip.returnTime,
      stays: trip.stays.map((s) => ({
        checkIn: s.checkIn,
        checkOut: s.checkOut,
        cityTier: s.cityTier ?? resolveCityTier(s.city, policy.cityTiers),
        lodgingInLieu: s.lodgingInLieu,
      })),
      dayTripCityTier: destination ? resolveCityTier(destination, policy.cityTiers) : null,
      personalDays: personal,
      mealsProvided: trip.mealsProvided,
      rule: policy.perDiemRule,
      perDiemRates: ratesFor(perDiemRows),
      lodgingInLieuRates: ratesFor(rowsFor('lodging_in_lieu')),
      currency: policy.currency,
    });
  }

  return {
    entitlements: {
      gradeRankUsed: Number.isFinite(rank) ? rank : null,
      rows,
      selfBookingAllowed,
    },
    violations,
    warnings,
    requiresJustification: trip.isPostFacto || violations.some((v) => v.severity === 'soft'),
    estimatedPerDiem,
    stayLimits,
    gradeMissing,
  };
}

/** The hard violations: what stops a submission unless a `travel:approve` holder overrides (D24). */
export function hardViolations(evaluation: TravelPolicyEvaluation): TravelPolicyViolation[] {
  return evaluation.violations.filter((v) => v.severity === 'hard');
}
